import axios from "axios";

// Use dynamic backend URL for all environments - matches api.js configuration
const getBaseURL = () => {
  // Check if we're in development and use local backend
  if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
    return 'http://localhost:8001/api';
  }
  
  // Use current domain for production/preview - this ensures frontend and backend are on same environment
  const currentProtocol = window.location.protocol;
  const currentHost = window.location.host;
  return `${currentProtocol}//${currentHost}/api`;
};

const API = getBaseURL();

// Token management
export const getToken = () => localStorage.getItem("auth_token");
export const setToken = (token) => localStorage.setItem("auth_token", token);
export const removeToken = () => localStorage.removeItem("auth_token");

// User management
export const getUser = () => {
  const user = localStorage.getItem("auth_user");
  return user ? JSON.parse(user) : null;
};

export const setUser = (user) => localStorage.setItem("auth_user", JSON.stringify(user));
export const removeUser = () => localStorage.removeItem("auth_user");

// Auth API
export const authAPI = {
  login: async (credentials) => {
    const response = await axios.post(`${API}/auth/login`, credentials);
    const { access_token, user } = response.data;
    setToken(access_token);
    setUser(user);
    return { token: access_token, user };
  },
  
  logout: () => {
    removeToken();
    removeUser();
  },
  
  register: async (userData) => {
    const token = getToken();
    const response = await axios.post(`${API}/auth/register`, userData, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },
  
  me: async () => {
    const token = getToken();
    const response = await axios.get(`${API}/auth/me`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  }
};

// User management API
export const userAPI = {
  getAll: async () => {
    const token = getToken();
    const response = await axios.get(`${API}/users`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },
  
  getById: async (id) => {
    const token = getToken();
    const response = await axios.get(`${API}/users/${id}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },
  
  update: async (id, userData) => {
    const token = getToken();
    const response = await axios.put(`${API}/users/${id}`, userData, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },
  
  delete: async (id) => {
    const token = getToken();
    const response = await axios.delete(`${API}/users/${id}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  },

  updatePermissions: async (id, permissions) => {
    const token = getToken();
    const response = await axios.put(`${API}/users/${id}/permissions`, { permissions }, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  }
};

// Check if user has admin role
export const isAdmin = (user) => user?.role === "admin";
export const isStaff = (user) => user?.role === "staff";
export const canEditCustomers = (user) => isAdmin(user);
export const canEditSubscriptions = (user) => isAdmin(user);
export const canEditInteractions = (user) => isAdmin(user) || isStaff(user);
export const canEditAssets = (user) => isAdmin(user) || isStaff(user);